// useCustomHook.js
import { CLASSY_FONT_FAMILY_DESIGN_TOKENS, CLASSY_DESIGN_TOKENS } from 'css/classyDesignTokens'

import {
  CssCustomProperties,
  DEFAULT_THEME_COLORS,
  DEFAULT_FOCUS_STYLES,
  DEFAULT_BORDER_STYLES,
  DEFAULT_FONT_STYLES,
} from 'css/theme'

import { StudioCampaignPages } from 'models/StudioCampaign'

import '@fontsource/domine'
import '@fontsource/inter'
import '@fontsource/karla'
import '@fontsource/lato'
import '@fontsource/merriweather'
import '@fontsource/montserrat'
import '@fontsource/open-sans'
import '@fontsource/oswald'
import '@fontsource/poppins'
import '@fontsource/pt-sans'
import '@fontsource/pt-sans-narrow'
import '@fontsource/pt-sans-caption'
import '@fontsource/raleway'
import '@fontsource/source-sans-3'
import '@fontsource/slabo-13px' // Defaults to weight 400
import '@fontsource/slabo-27px' // Defaults to weight 400

const getTheme = (pages?: StudioCampaignPages) => {
  // Merge CLASSY_FONT_FAMILY_DESIGN_TOKENS and CLASSY_DESIGN_TOKENS into pages.theme
  const themeExtended: { [key: string]: string } = {
    ...pages?.theme,
    ...CLASSY_FONT_FAMILY_DESIGN_TOKENS,
    ...CLASSY_DESIGN_TOKENS,
  }

  const dereference = (theme: typeof themeExtended, key: string): string | undefined => {
    if (!theme) return undefined

    const value = theme[key]

    // If the value does not start with var(--), it is not a reference to another value
    if (!value?.startsWith('var(--')) {
      return value
    }

    // If the value starts with var(--), we need to dereference it
    // We need to remove the var(-- and the closing parenthesis )
    const dereferencedKey = value.slice(4, -1)

    // If the dereferencedKey is not in the theme, we return undefined
    // This will cause the default value to be used
    if (!theme[dereferencedKey]) {
      return undefined
    }

    // Otherwise, call dereference again with the dereferencedKey
    return dereference(theme, dereferencedKey)
  }

  // For each theme DEFAULT_THEME_COLORS, we need to check if the theme has a value for it
  // If it does, we override the default value, otherwise, we keep the default value
  const themeColors = Object.keys(DEFAULT_THEME_COLORS).reduce(
    (acc: CssCustomProperties, key: string) => {
      const themeColor = dereference(themeExtended, key)

      if (themeColor) {
        acc[key as keyof CssCustomProperties] = themeColor
      }

      return acc
    },
    DEFAULT_THEME_COLORS,
  )

  // Do the same for DEFAULT_FOCUS_STYLES
  const focusStyles = Object.keys(DEFAULT_FOCUS_STYLES).reduce(
    (acc: CssCustomProperties, key: string) => {
      const focusStyle = dereference(themeExtended, key)

      if (focusStyle) {
        acc[key as keyof CssCustomProperties] = focusStyle
      }

      return acc
    },
    DEFAULT_FOCUS_STYLES,
  )

  // And for DEFAULT_BORDER_STYLES
  const borderStyles = Object.keys(DEFAULT_BORDER_STYLES).reduce(
    (acc: CssCustomProperties, key: string) => {
      const borderStyle = dereference(themeExtended, key)

      if (borderStyle) {
        acc[key as keyof CssCustomProperties] = borderStyle
      }

      return acc
    },
    DEFAULT_BORDER_STYLES,
  )

  // And for DEFAULT_FONT_STYLES
  const fontStyles = Object.keys(DEFAULT_FONT_STYLES).reduce(
    (acc: CssCustomProperties, key: string) => {
      const fontStyle = dereference(themeExtended, key)

      if (fontStyle) {
        acc[key as keyof CssCustomProperties] = fontStyle
      }

      return acc
    },
    DEFAULT_FONT_STYLES,
  )

  // Merge themeColors, focusStyles, borderStyles, and fontStyles into one object
  const themeDefinition = {
    ...themeColors,
    ...focusStyles,
    ...borderStyles,
    ...fontStyles,
  }

  return themeDefinition
}

export default getTheme
